/**
 * NewArea - Create a new area within a project
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';

const NewArea = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  
  const [project, setProject] = useState(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    
    const loadProject = async () => {
      try {
        setLoading(true);
        const data = await api.getProject(projectId);
        if (!cancelled) {
          setProject(data.project);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load project');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    
    loadProject();
    
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Area name is required');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      const area = await api.createArea(projectId, { name: name.trim() });
      navigate(`/project/${projectId}/area/${area.id}`);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <Link to={`/project/${projectId}`} className="text-slate-400 hover:text-white text-sm flex items-center gap-2 mb-6">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to {project?.name || 'Project'}
      </Link>
      
      <h1 className="text-2xl font-bold text-white mb-6">New Area</h1>
      
      <form onSubmit={handleSubmit}>
        {error && (
          <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 text-red-400 mb-6">
            {error}
          </div>
        )}
        
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 mb-6">
          <label className="block text-slate-400 text-sm mb-2">
            Area Name <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Main Roof - Warm Single Ply"
            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 disabled:opacity-50"
            disabled={saving || loading}
            autoFocus
          />
          <p className="text-slate-500 text-sm mt-2">
            Describe the roof area, e.g. "Balconies - Cold RBM" or "Plot 1-10 Main Roofs"
          </p>
        </div>

        <div className="flex justify-end gap-4">
          <Link
            to={`/project/${projectId}`}
            className="px-6 py-2 text-slate-400 hover:text-white transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium px-6 py-2 rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? 'Creating...' : 'Create Area'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewArea;
